import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export type AdminAuthState =
  | "loading"
  | "login"
  | "checking-admin"
  | "authenticated"
  | "not-admin";

export function useAdminAuth() {
  const [authState, setAuthState] = useState<AdminAuthState>("loading");
  const [loginError, setLoginError] = useState("");

  // Verifica se a conta autenticada existe na tabela admin_users.
  // A RLS de admin_users já limita a leitura ao próprio registro (user_id = auth.uid()),
  // então o retorno só traz a linha se a pessoa for admin de fato.
  const verifyAdmin = useCallback(async (): Promise<boolean> => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) return false;

    const { data: adminRow, error } = await supabase
      .from("admin_users")
      .select("user_id")
      .maybeSingle();

    return !error && !!adminRow;
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function checkSession() {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (cancelled) return;

      if (!session) {
        setAuthState("login");
        return;
      }

      setAuthState("checking-admin");
      const isAdmin = await verifyAdmin();
      if (cancelled) return;
      setAuthState(isAdmin ? "authenticated" : "not-admin");
    }

    checkSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (cancelled) return;
      // Se a sessão expirar ou o usuário sair, volta pra tela de login sem erro feio.
      if (event === "SIGNED_OUT") {
        setAuthState("login");
        setLoginError("");
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [verifyAdmin]);

  const login = useCallback(
    async (email: string, password: string) => {
      setLoginError("");

      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (error) {
        // Nunca deduzir outra causa — sempre a mesma mensagem honesta.
        setLoginError("E-mail ou senha incorretos");
        return;
      }

      const isAdmin = await verifyAdmin();
      setAuthState(isAdmin ? "authenticated" : "not-admin");
    },
    [verifyAdmin],
  );

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setAuthState("login");
    setLoginError("");
  }, []);

  return { authState, loginError, login, logout };
}